import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { catchError, of, switchMap, tap } from 'rxjs';

import { AuthPageLayoutComponent } from '../../../shared/layout/auth-page-layout/auth-page-layout.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { AuthService } from '../../../shared/services/auth.service';
import { OperatorsService } from '../../../shared/services/operators.service';
import { ZonesService } from '../../../shared/services/zones.service';
import {
  LICENSE_TIER_OPTIONS,
  LicenseTier,
  OPERATOR_STATUS_META,
} from '../../../shared/types/operator.types';
import { TPipe } from '../../../shared/pipe/t.pipe';

/** Vue minimale de l'opérateur existant (candidature déjà déposée). */
interface ExistingOperator {
  businessName: string | null;
  zoneCode: string | null;
  operatorStatus: 'PENDING' | 'ACTIVE' | 'EXHAUSTED' | 'SUSPENDED';
}

/**
 * Candidature opérateur (`/apply`) — accessible à tout utilisateur authentifié
 * (authGuard seul, hors `operatorAreaGuard`). C'est le point d'entrée d'un user
 * qui n'a pas encore le rôle OPERATOR (le shell `/` le redirige ici).
 *
 * Flow :
 *   1. Si déjà rôle OPERATOR → redirect `/` (rien à faire).
 *   2. Sinon ping `GET /operators/me/dashboard` :
 *      - 404 → pas d'opérateur → **formulaire de candidature**.
 *      - 200 + PENDING → **écran « en attente d'approbation »**.
 *      - 200 + ACTIVE/EXHAUSTED/SUSPENDED → rôle censé être présent mais JWT
 *        périmé → refresh + `/me` → si rôle obtenu, redirect `/`, sinon écran
 *        d'attente.
 *   3. Submit `POST /operators/apply` → écran d'attente (le rôle n'est PAS
 *      assigné à la candidature, seulement à l'approbation admin).
 */
@Component({
  selector: 'app-apply',
  standalone: true,
  imports: [TPipe, 
    ReactiveFormsModule,
    RouterModule,
    AuthPageLayoutComponent,
    PrettySelectComponent,
  ],
  templateUrl: './apply.component.html',
})
export class ApplyComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly operators = inject(OperatorsService);
  private readonly zonesService = inject(ZonesService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly statusMeta = OPERATOR_STATUS_META;
  readonly licenseTierOptions: PrettySelectOption<LicenseTier>[] = LICENSE_TIER_OPTIONS.map(
    (o) => ({ value: o.value, label: o.label }),
  );

  readonly phase = signal<'checking' | 'form' | 'submitting' | 'pending' | 'done' | 'error'>(
    'checking',
  );
  readonly error = signal<string | null>(null);
  readonly checkError = signal<string | null>(null);

  /** Options zone (chargées via l'endpoint public). */
  readonly zoneOptions = signal<PrettySelectOption<string>[]>([]);
  readonly zonesError = signal<string | null>(null);

  /** Renseigné quand une candidature existe déjà (écran d'attente). */
  readonly existing = signal<ExistingOperator | null>(null);

  readonly form = this.fb.group({
    businessName: ['', [Validators.required, Validators.minLength(2)]],
    zoneCode: ['', [Validators.required]],
    licenseTier: [null as LicenseTier | null],
    phone: [''],
    email: [''],
  });

  ngOnInit(): void {
    // Déjà opérateur (rôle présent) → on file au dashboard.
    if (this.auth.hasRole('OPERATOR')) {
      this.router.navigateByUrl('/', { replaceUrl: true });
      return;
    }

    // Pré-remplir email/téléphone depuis le profil courant.
    const me = this.auth.user();
    if (me?.email) this.form.controls.email.setValue(me.email);
    if (me?.phone) this.form.controls.phone.setValue(me.phone);

    this.checkStatus();
  }

  /** Bouton « réessayer » de l'écran d'erreur. */
  retry(): void {
    this.checkError.set(null);
    this.phase.set('checking');
    this.checkStatus();
  }

  /** Détermine l'état (formulaire / attente / entrée app) selon l'opérateur existant. */
  private checkStatus(): void {
    // Existe-t-il déjà une candidature pour ce user ?
    this.operators
      .myDashboard()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => {
          if (d.operatorStatus === 'PENDING') {
            this.existing.set({
              businessName: d.businessName,
              zoneCode: d.zoneCode,
              operatorStatus: d.operatorStatus,
            });
            this.phase.set('pending');
          } else {
            // Statut actif/épuisé/suspendu mais on est ici sans le rôle → JWT périmé.
            this.existing.set({
              businessName: d.businessName,
              zoneCode: d.zoneCode,
              operatorStatus: d.operatorStatus,
            });
            this.refreshAndExit();
          }
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 404) {
            // Pas d'opérateur → formulaire.
            this.loadZones();
            this.phase.set('form');
            return;
          }
          this.checkError.set(toErrorMessage(err, 'errors.operatorStatusCheckFailed'));
          this.phase.set('error');
        },
      });
  }

  /** Charge la liste publique des zones pour le menu déroulant. */
  private loadZones(): void {
    this.zonesService
      .listPublic()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (zones) => {
          this.zoneOptions.set(
            zones.map((z) => ({ value: z.code, label: `${z.name} (${z.code})` })),
          );
          this.zonesError.set(null);
        },
        error: (err: unknown) => {
          this.zonesError.set(
            toErrorMessage(err, 'errors.zonesLoadFailed'),
          );
        },
      });
  }

  submit(): void {
    if (this.form.invalid || this.phase() === 'submitting') {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();

    this.phase.set('submitting');
    this.error.set(null);

    this.operators
      .apply({
        businessName: raw.businessName.trim(),
        zoneCode: raw.zoneCode,
        licenseTier: raw.licenseTier ?? null,
        phone: raw.phone.trim() || null,
        email: raw.email.trim() || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (op) => {
          // Le rôle OPERATOR n'est PAS encore assigné (attente d'approbation) →
          // on bascule sur l'écran d'attente, pas vers le dashboard.
          this.existing.set({
            businessName: op.businessName,
            zoneCode: op.zoneCode,
            operatorStatus: op.operatorStatus,
          });
          this.phase.set('pending');
        },
        error: (err: unknown) => {
          this.phase.set('form');
          this.error.set(
            toErrorMessage(err, 'errors.applicationFailed', {
              409: 'errors.apply.alreadyExists',
              400: 'errors.apply.checkFields',
            }),
          );
        },
      });
  }

  /**
   * Recharge la session (refresh + /me) pour récupérer le rôle OPERATOR
   * fraîchement assigné, puis entre dans l'app. Utilisé depuis l'écran d'attente
   * (« j'ai été approuvé ») et au boot quand le statut est déjà actif.
   */
  refreshAndExit(): void {
    this.auth
      .refresh()
      .pipe(
        switchMap(() => this.auth.me().pipe(catchError(() => of(null)))),
        tap(() => {
          if (this.auth.hasRole('OPERATOR')) {
            this.phase.set('done');
            this.router.navigateByUrl('/', { replaceUrl: true });
          } else if (this.phase() === 'checking') {
            // Pas encore le rôle (approbation non faite) → écran d'attente.
            this.phase.set('pending');
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => {
          if (this.phase() === 'checking') this.phase.set('pending');
        },
      });
  }

  logout(): void {
    this.auth.logout();
  }
}
